import { FC, useRef, useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import clsx from "clsx";
import Swal from "sweetalert2";
import { useDataTableStore } from "../../stores/DataTableStore";
import useAccessToken from "../../hooks/AccessToken";
import { Buffer } from 'buffer';
import { MeetingAttachment, VisitPermision } from "../../interfaces/Meeting";
import { updateVisitOutMeeting } from "../../api/MeetingsCRUD";
import moment from "moment";
import useUser from "../../hooks/User";
import { useSettingsStore } from "../../stores/SettingsStore";
import { KTSVG } from "../../_metronic/helpers";
import { dataURLtoFile, getDataById, getDateRangeDuration, getRandomFileName, handleCaps } from "../../functions/general";
import { useIntl } from "react-intl";
import { insertAttachment, insertMultiAttachment } from "../../api/ResourceAPI";
import Select from "react-select";
import useHandleCloseModal from "../../hooks/HandleCloseModal";
import { CapturePhoto } from "../Attendance/CapturePhoto";
import useCamera from "../../hooks/Camera";
import { CustomField } from "../../interfaces/Settings";
import { useMeetingStore } from "../../stores/MeetingsStore";
import { useCustomeStore } from "../../stores/CustomeStore";
import RenderCustomFieldGeneralForm from "../RenderComponent/RenderCustomFieldGeneralForm";
import { useMerchantStore } from "../../stores/MerchantStore";
import { usePipelineStore } from "../../stores/PipelineStore";
import { useProductStore } from "../../stores/ProductStore";
import { useUserStore } from "../../stores/UserStore";
import { getStatusMeeting } from "../../functions/meeting";

interface Props {
    codeUrl: string
    idMeeting: string
    visit: VisitPermision
    setVisit: (data: VisitPermision) => void;
}

const UpdateVisitMeeting: FC<Props> = ({ codeUrl, idMeeting, visit, setVisit }) => {
    const {
        selectMultiMeeting,
        simpleMeetings,
    } = useMeetingStore();
    const user = useUser()
    const camera = useCamera()
    const [modalBtnLoading, setModalBtnLoading] = useState<boolean>(false);
    const modalRef = useRef<HTMLDivElement | null>(null);
    const closeModalRef = useRef<HTMLButtonElement>(null);
    const token = useAccessToken()
    const { tablesData, setTablesData } = useDataTableStore()
    const intl = useIntl()
    const { simpleMerchants } = useMerchantStore()
    const { simplePipelines } = usePipelineStore()
    const { simpleProducts } = useProductStore()
    const { settings } = useSettingsStore()
    const { users } = useUserStore()
    const [capturePhoto, setCapturePhoto] = useState(null);
    const [isShowCamera, setIsShowCamera] = useState<boolean>(false);
    const [customFields, setCustomFields] = useState<CustomField>({})
    const [customFieldsMeratus, setCustomFieldsMeratus] = useState<CustomField>({})
    const [customFieldsBaruBLA, setCustomFieldsBaruBLA] = useState<CustomField>({})
    const [customFieldsLamaBLA, setCustomFieldsLamaBLA] = useState<CustomField>({})
    const { companyId } = useCustomeStore()

    const formSchema = Object.keys(customFields).reduce((obj: any, item: string) => {
        const field = customFields[item];
        const isRequiredMessage = intl.formatMessage({ id: "FORM.VALIDATION.FIELD_REQUIRED" }, { title: field.name });

        if (item === '4af76875-862a-4533-b980-635f57853799') {
            obj[item] = field.required
                ? Yup.string().required(isRequiredMessage).nullable()
                : Yup.string().notRequired().nullable();
        }

        return obj;
    }, {});

    const validationSchema = Yup.object().shape({
        location: Yup.string().required(intl.formatMessage({ id: "FORM.VALIDATION.FIELD_REQUIRED" }, { title: intl.formatMessage({ id: "FORM.LABEL.LOCATION" }) })),
        latitude: Yup.number().required(intl.formatMessage({ id: "FORM.VALIDATION.FIELD_REQUIRED" }, { title: 'Latitude' })),
        longitude: Yup.number().required(intl.formatMessage({ id: "FORM.VALIDATION.FIELD_REQUIRED" }, { title: 'Longitude' })),
        attachment: Yup.string().nullable().test('is-required', intl.formatMessage({ id: "FORM.VALIDATION.FIELD_REQUIRED" }, { title: intl.formatMessage({ id: "FORM.LABEL.PHOTO" }, { title: settings.meeting_title }) }), function (value) {
            if (!settings.meeting_visit_disable_capture_camera) {
                return !!value;
            }
            return true;
        }), ...formSchema
    });

    const initialValues = {
        location: "",
        latitude: 0,
        longitude: 0,
        attachment: "",
        note: "",
        ...Object.fromEntries(Object.keys(customFields).map((key) => [key,
            visit.customFields && visit.customFields[key] ?
                customFields[key].type === 'date' ?
                    moment(visit.customFields[key], 'YYYY-MM-DD', true).isValid() ?
                        visit.customFields[key]
                        : null
                    : visit.customFields[key]
                : ""]
        )),
    };

    const formik = useFormik({
        initialValues: initialValues,
        validationSchema: validationSchema,
        enableReinitialize: true,
        onSubmit: async (values, { setStatus, setSubmitting }) => {
            setModalBtnLoading(true)

            const convertCustomFields: Record<string, any> = Object.keys(customFields).reduce(
                (obj, item) => {
                    if (!obj.info_custom_fields) {
                        obj.info_custom_fields = {};
                    }

                    if (customFields[item].type === 'merchant') {
                        const selectedMerchants = getDataById(values[item as keyof typeof values] as any, simpleMerchants);
                        obj[`custom_fields.${item}`] = selectedMerchants?.map((merchant: any) => merchant.name).join(", ");
                        obj.info_custom_fields[item] = selectedMerchants?.map((merchant: any) => ({
                            merchant_template_id: merchant.merchantTemplateId,
                            merchant_id: merchant.id,
                            merchant_name: merchant.name
                        })) || [];
                    }
                    else if (customFields[item].type === 'user') {
                        const selectedUsers = getDataById(values[item as keyof typeof values] as any, users);
                        obj[`custom_fields.${item}`] = selectedUsers?.map((user: any) => user.name).join(", ");
                        obj.info_custom_fields[item] = selectedUsers?.map((user: any) => ({
                            id: user.id,
                            name: user.name
                        })) || [];
                    }
                    else if (customFields[item].type === 'pipeline') {
                        const selectedPipeline = getDataById(values[item as keyof typeof values] as string, simplePipelines);
                        obj[`custom_fields.${item}`] = selectedPipeline ?
                            `${selectedPipeline.name} / ${selectedPipeline.nominal} / ${moment(selectedPipeline.dateCreated).format('DD MMM YYYY')}`
                            : '-';
                        obj.info_custom_fields[item] = selectedPipeline ? [{
                            id: selectedPipeline.id,
                            name: selectedPipeline.name,
                            nominal: selectedPipeline.nominal,
                            dateCreated: selectedPipeline.dateCreated
                        }] : [];
                    }
                    else if (customFields[item].type === 'meeting') {
                        const selectedMeeting: any[] = getDataById(values[item as keyof typeof values] as string, simpleMeetings);
                        obj[`custom_fields.${item}`] = selectedMeeting && selectedMeeting.length > 0 ?
                            selectedMeeting.map((meeting: any) => `${meeting.name} / ${moment(meeting.dateMeet).format('DD MMM YYYY')} / ${meeting.meetingStatus}`).join(', ')
                            : '-';
                        obj.info_custom_fields[item] = selectedMeeting && selectedMeeting.length > 0 ? selectedMeeting.map((meeting: any) => ({
                            meeting_template_id: meeting.meetingTemplateId,
                            id: meeting.id,
                            merchant_name: meeting.name,
                            date_meet: meeting.dateMeet,
                            meeting_status: meeting.meetingStatus
                        })) : [];
                    } else if (customFields[item].type === 'product') {
                        const selectedProducts = getDataById(values[item as keyof typeof values] as any, simpleProducts);
                        obj[`custom_fields.${item}`] = selectedProducts?.map((product: any) => product.name).join(", ");
                        obj.info_custom_fields[item] = selectedProducts?.map((product: any) => ({
                            product_id: product.id,
                            product_name: product.name,
                            product_prices: product.product_prices,
                        })) || [];
                    }
                    else {
                        obj[`custom_fields.${item}`] = values[item as keyof typeof values] || '';
                        obj.info_custom_fields[item] = [];
                    }

                    return obj;
                },
                {} as Record<string, any>
            );

            // Assuming values is of type Record<string, string> and customFields is of type fieldCustom
            const customFieldsValues: Record<string, string | string[] | null | number> = Object.keys(customFields).reduce(
                (obj, item) => ({
                    ...obj,
                    [item]: (values as Record<string, string | string[] | null | number>)[item],
                }),
                {}
            );

            // Use Promise.all to await all async operations
            await Promise.all(
                Object.keys(customFields).map(async (key) => {
                    if (customFields[key].type === 'image' || customFields[key].type === 'file' || customFields[key].type === 'video') {
                        try {
                            if (typeof values[key as keyof typeof values] === 'object') {
                                const resourceResponse = await insertAttachment(values[key as keyof typeof values] as any, 'MEETING', token);
                                customFieldsValues[key] = resourceResponse.data.data.length > 0 ? resourceResponse.data.data[0] : "";
                            }
                        } catch (error) {
                            console.log(error);
                        }
                    } else if (customFields[key].type === 'images' || customFields[key].type === 'files' || customFields[key].type === 'videos') {
                        try {
                            if (values[key as keyof typeof values]) {
                                var dataMultiAttachment = (values[key as keyof typeof values] as any).filter((item: any) => typeof item !== 'string')
                                var dataUrlAttachment = (values[key as keyof typeof values] as any).filter((item: any) => typeof item === 'string')

                                const resourceResponse = await insertMultiAttachment(dataMultiAttachment, 'MEETING', token);
                                (customFieldsValues[key] as any) = resourceResponse.data.data.length > 0 ? [...dataUrlAttachment, ...resourceResponse.data.data] : dataUrlAttachment.length > 0 ? dataUrlAttachment : null;
                            }
                        } catch (error) {
                            console.log(error);
                        }
                    } else if (customFields[key].type === 'multiple' || customFields[key].type === 'multipleOption') {
                        var multipleValues = (values[key as keyof typeof values] as any)?.length! > 0 ? values[key as keyof typeof values] : null;
                        customFieldsValues[key] = multipleValues as string
                    } else if (customFields[key].type === 'dateTimeRange') {
                        if (values[key as keyof typeof values]) {
                            const [startDate, endDate] = values[key as keyof typeof values] as any;
                            customFieldsValues[key] = [
                                startDate ? (new Date(startDate).toISOString() as any) : null,
                                endDate ? (new Date(endDate).toISOString() as any) : null
                            ];
                        } else {
                            customFieldsValues[key] = null;
                        }
                    }
                })
            );

            try {
                let attachments: MeetingAttachment[] = []
                if (values.attachment) {
                    const resourceResponse = await insertAttachment(values.attachment, 'MEETING', token)
                    if (resourceResponse.data.data.length > 0) {
                        const parts = resourceResponse.data.data[0].split('/');
                        const fileName = parts[parts.length - 1];
                        attachments = [
                            {
                                name: fileName,
                                url: resourceResponse.data.data[0]
                            }
                        ]
                    }
                }

                const response = await updateVisitOutMeeting({
                    meetingTemplateId: codeUrl,
                    idMeeting: idMeeting,
                    token: token,
                    note: values.note,
                    location: values.location,
                    latitude: values.latitude,
                    longitude: values.longitude,
                    attachmentLink: attachments.length > 0 ? attachments[0].url : null,
                    customFields: customFieldsValues
                })
                if (response.data.success) {
                    Swal.fire({
                        icon: 'success',
                        heightAuto: false,
                        title: `${intl.formatMessage({ id: "MEETING.ACTION.VISIT_OUT" })}.`,
                        timer: 2000,
                        timerProgressBar: true
                    })

                    setTablesData('meetings', (tablesData.meetings.map(item => {
                        if (item.id === idMeeting) {
                            return {
                                ...item,
                                ...convertCustomFields['custom_fields'],
                                info_custom_fields: convertCustomFields.info_custom_fields,
                                custom_fields: response.data.data.custom_fields,
                                meeting_status: getStatusMeeting(response.data.data, intl),
                                attachments: response.data.data.attachments,
                                note: response.data.data.note,
                                date_meet: moment(response.data.data.date_meet).format("YYYY-MM-DD HH:mm:ss"),
                                date_meet_end: response.data.data.allDay ? moment(response.data.data.date_meet_end).subtract(1, 'days').local().format("YYYY-MM-DD HH:mm:ss") : moment(response.data.data.date_meet_end).format("YYYY-MM-DD HH:mm:ss"),
                                logs: response.data.data.logs,
                                duration: getDateRangeDuration(response.data.data.date_meet, response.data.data.date_meet_end),
                            }
                        } else {
                            return item
                        }
                    })));

                    closeModalRef.current?.click();
                    setVisit({
                        permission: true,
                        id: '',
                        customFields: {}
                    })
                }
            } catch (error: any) {
                Swal.fire({
                    icon: 'error',
                    title: error.response.data.message,
                    confirmButtonText: 'Ok',
                    heightAuto: false,
                })
            } finally {
                setModalBtnLoading(false)
            }
        }
    });

    const handleCoordinate = async () => {
        try {
            if ("geolocation" in navigator) {
                navigator.geolocation.getCurrentPosition(
                    (position) => {
                        formik.setFieldValue("latitude", position.coords.latitude);
                        formik.setFieldValue("longitude", position.coords.longitude);

                        handleReversLatLongToAddress(position.coords.latitude, position.coords.longitude);
                    },
                    (error) => {
                        if (error.code === error.PERMISSION_DENIED) {
                            var message1 = intl.formatMessage({ id: "BROWSER.GEOLOCATION.TUTORIAL" })
                            alertErrorCoordinate(message1)
                        }
                    }
                );
            } else {
                var message2 = intl.formatMessage({ id: "BROWSER.SUPPORT.GEOLOCATION" })
                alertErrorCoordinate(message2);
            }
        } catch (error) {
            var message3 = intl.formatMessage({ id: "BROWSER.GEOLOCATION.PERMISSION_DENIED" })
            alertErrorCoordinate(message3)
        }
    };

    const handleReversLatLongToAddress = (lat: number, long: number) => {
        fetch(`https://tool.looyal.id/general/gmaps/convert-latlong?lat=${lat}&long=${long}`)
            .then(response => response.json())
            .then(data => {
                formik.setFieldValue("location", data.data);
            })
            .catch((error) => {
                console.log(error)
            });
    }

    const alertErrorCoordinate = (message: string) => {
        Swal.fire({
            icon: 'error',
            title: intl.formatMessage({ id: "ALERT.TITLE.ERROR" }),
            html: message,
            confirmButtonText: 'Ok',
            target: document.getElementById(`update-visit-meeting-modal-${idMeeting}`),
            heightAuto: false,
        });
        formik.setFieldValue("location", "");
    }

    const handleTakePhoto = async () => {
        if (capturePhoto) {
            const img = new Image();
            img.onload = async () => {
                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');
                if (ctx) {
                    canvas.width = img.width;
                    canvas.height = img.height;
                    ctx.drawImage(img, 0, 0);

                    const locationText = formik.values.location;
                    const now = new Date();
                    const dateText = moment(now).format("DD MMMM YYYY HH:mm:ss")

                    const margin = 5;
                    let fontSize = Math.max(10, Math.min(14, img.width / 50));
                    ctx.font = `${fontSize}px Arial`;
                    ctx.fillStyle = 'white';
                    ctx.textAlign = 'right';
                    ctx.textBaseline = 'bottom';
                    ctx.strokeStyle = 'black';
                    ctx.lineWidth = 1;

                    const wrapText = (text: string, maxWidth: number) => {
                        const words = text.split(' ');
                        let lines = [];
                        let currentLine = '';
                        for (let i = 0; i < words.length; i++) {
                            const testLine = currentLine ? `${currentLine} ${words[i]}` : words[i];
                            const metrics = ctx.measureText(testLine);
                            if (metrics.width > maxWidth && i > 0) {
                                lines.push(currentLine);
                                currentLine = words[i];
                            } else {
                                currentLine = testLine;
                            }
                        }
                        lines.push(currentLine);
                        return lines;
                    };

                    const maxWidth = img.width - 2 * margin;
                    const locationLines = wrapText(locationText, maxWidth);
                    let yPos = img.height - margin;
                    const lineHeight = fontSize * 1.2;

                    ctx.strokeText(dateText, img.width - margin, yPos);
                    ctx.fillText(dateText, img.width - margin, yPos);
                    yPos -= lineHeight;

                    for (let i = locationLines.length - 1; i >= 0; i--) {
                        ctx.strokeText(locationLines[i], img.width - margin, yPos);
                        ctx.fillText(locationLines[i], img.width - margin, yPos);
                        yPos -= lineHeight;
                    }

                    const labeledPhotoDataURL = canvas.toDataURL('image/jpeg');
                    const file = dataURLtoFile(labeledPhotoDataURL, getRandomFileName() + '.jpeg', Buffer);

                    formik.setFieldValue('attachment', file);

                }
            };

            img.src = capturePhoto;
        } else {
            formik.setFieldValue('attachment', null);
        }
    };

    useEffect(() => {
        // Get the modal element
        const modalElement = modalRef.current;

        // Define the event listeners
        const handleModalHidden = () => {
            formik.setFieldValue("attachment", "");
            formik.setFieldValue("note", '');
            setIsShowCamera(false)
        };

        // Attach the event listeners
        modalElement?.addEventListener('hidden.bs.modal', handleModalHidden);

        // Cleanup
        return () => {
            modalElement?.removeEventListener('hidden.bs.modal', handleModalHidden);
        };
    }, [formik]);

    useEffect(() => {
        handleCoordinate()
    }, [])

    useEffect(() => {
        if (Object.keys(selectMultiMeeting).length !== 0) {
            setCustomFields(selectMultiMeeting.customFields)
            // Only Visible Custom Field tertentu // 
            if (user.data.company_id === companyId.meratus) {
                /*
                Produk Vas Yang di approach: 40747ad5-b64d-45de-a8b7-19f83caddcfb
                Tertarik Produk Vas: b09f540c-bb73-42fb-8c91-3f5d702bf463
                */
                const customFieldsMeratus = Object.entries(selectMultiMeeting.customFields).filter(([key, value]) => key === '40747ad5-b64d-45de-a8b7-19f83caddcfb' || key === 'b09f540c-bb73-42fb-8c91-3f5d702bf463')
                const result = customFieldsMeratus.reduce((acc: any, [key, value]: any) => {
                    acc[key] = value
                    return acc
                }, {}) as CustomField
                setCustomFieldsMeratus(result)
            } else if (user.data.company_id === companyId.bla && codeUrl === "b4f80f98-11bc-49c8-b88f-df7726833bc5") { //Database Kunjungan Baru
                const idCustomField = ["45de2c5f-07e5-4900-b903-97085afa5018", "82a21d1a-ca04-43fe-88f5-77c76ef4ec2c", "2b25d0c1-adad-49ce-8026-8bb710606a3e", "24d4df94-f0ce-4839-8c68-ad40f51c4fff", "b216e3a3-f536-47c7-bc88-1cb62b5d67e9", "affab01d-2ff7-48e3-bc92-6bc836d4b2d4", "749602a2-8463-450d-93af-dee711e27c1c", "e0c4a696-9598-470b-b876-b12e771aac29", "492410f2-8aa7-411d-8579-645417a62ebc", "b2cd76f1-9210-4fca-aa67-35f5e08c0261", "cb0202fa-c4a9-4c4b-91b3-b6297c49c9bc", "d777a295-c9d7-458f-a051-d74e9d6961f3"]
                const customFieldsBaruBLA = Object.entries(selectMultiMeeting.customFields).filter(([key, value]) => idCustomField.includes(key))
                const result = customFieldsBaruBLA.reduce((acc: any, [key, value]: any) => {
                    acc[key] = value
                    return acc
                }, {}) as CustomField
                setCustomFieldsBaruBLA(result)
            } else if (user.data.company_id === companyId.bla && codeUrl === "68372818-b7ae-42d0-982d-9c2bff5ceee1") { //Database Kunjungan Lama
                const idCustomField = ["bb4e1bde-627a-449f-8fe2-9246dd4dfd82", "6b100575-08c5-4fb3-958d-2783cb9ae35e", "5c3f3a27-ce45-475f-bef1-02f4af57febb", "a38eb3e4-7434-42cd-a647-d9fb8cbdfaf2", "d26b5d04-c724-439b-9a68-b346627e8286", "131ee97a-0f61-4099-a3c3-c66da98e5d29", "9c929cde-8b4f-4bb8-862a-9ae07eac776a", "1a160444-e3c3-4814-9614-b31de3f8ae7d", "88787ce7-053d-4993-bdb5-3d13722d6059", "d3762c19-665b-4331-b462-9554006b988a", "37915b0c-9cf9-4764-a126-10e2dd11524d", "1c952b39-6566-4af5-b535-8389376ae4d8"]
                const customFieldsLamaBLA = Object.entries(selectMultiMeeting.customFields).filter(([key, value]) => idCustomField.includes(key))
                const result = customFieldsLamaBLA.reduce((acc: any, [key, value]: any) => {
                    acc[key] = value
                    return acc
                }, {}) as CustomField
                setCustomFieldsLamaBLA(result)
            }
        }
    }, [selectMultiMeeting])

    const { handleCloseModal } = useHandleCloseModal(modalRef, closeModalRef, formik);

    return (
        <>
            <button type="button"
                className="btn btn-primary mt-3"
                data-bs-toggle="modal"
                data-bs-target={`#update-visit-meeting-modal-${idMeeting}`}
                onClick={() => setIsShowCamera(true)}
            >
                <i className="fas fa-sign-out-alt"></i> {intl.formatMessage({ id: 'MEETING.ACTION.VISIT_OUT' })}
            </button>

            <div className="modal fade" tabIndex={-1} id={`update-visit-meeting-modal-${idMeeting}`} ref={modalRef}>
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title" style={{ overflowWrap: "anywhere" }}>{intl.formatMessage({ id: 'MEETING.ACTION.VISIT_OUT' })}</h5>
                            <div
                                className="btn btn-icon btn-sm btn-active-light-primary ms-2"
                                onClick={handleCloseModal}
                            >
                                <KTSVG
                                    path="/media/icons/duotune/arrows/arr061.svg"
                                    className="svg-icon svg-icon-2x"
                                />
                            </div>
                        </div>
                        <form onSubmit={formik.handleSubmit} noValidate>
                            <div className="modal-body">
                                {/* Coordinat Location */}
                                <span className='form-label fs-6 fw-bolder text-dark required'>{intl.formatMessage({ id: 'FORM.LABEL.LOCATION' })}</span><br />
                                <small>{intl.formatMessage({ id: 'FORM.GUIDE.ACTIVE_GPS' })}</small>
                                <div className="d-flex">
                                    <div className="input-group mb-3">
                                        <input
                                            {...formik.getFieldProps('location')}
                                            className={clsx(
                                                'form-control form-control-lg me-3',
                                                { 'is-invalid': formik.touched.location && formik.errors.location },
                                            )}
                                            type='text'
                                            name='location'
                                            autoComplete='off'
                                            disabled={true}
                                        />
                                    </div>
                                    <button type="button" className="btn btn-secondary mb-3" onClick={handleCoordinate}>
                                        <i className="fas fa-search"></i>
                                    </button>
                                </div>
                                {formik.touched.location && formik.errors.location && (
                                    <div className='fv-plugins-message-container text-danger'>
                                        <span role='alert' className="text-danger">{formik.errors.location}</span>
                                    </div>
                                )}

                                {/* Attachments */}
                                {
                                    !settings.meeting_visit_disable_capture_camera &&
                                    <>
                                        <label className={`form-label fs-6 fw-bolder text-dark required`}>{intl.formatMessage({ id: 'FORM.LABEL.PHOTO' }, { title: settings.meeting_title })}</label>
                                        {
                                            camera.isCamera && isShowCamera ?
                                                <CapturePhoto keyString='visit' img={capturePhoto!} setImg={setCapturePhoto!} func={handleTakePhoto} modalBtnLoading={modalBtnLoading!} />
                                                :
                                                <div className='mb-5 alert alert-danger'>
                                                    <div className='alert-text font-weight-bold'>
                                                        {intl.formatMessage({ id: 'ATTENDANCE.PERMISSIONS.ALERT1' }, { title: intl.formatMessage({ id: "ATTENDANCE.PERMISSIONS.CAMERA" }) })}<br /><br />
                                                        <b>{intl.formatMessage({ id: 'ATTENDANCE.PERMISSIONS.ALERT2' }, { title: intl.formatMessage({ id: "ATTENDANCE.PERMISSIONS.CAMERA" }) })}</b><br />
                                                        {intl.formatMessage({ id: 'ATTENDANCE.PERMISSIONS.ALERT3' })}<br />
                                                        {intl.formatMessage({ id: 'ATTENDANCE.PERMISSIONS.ALERT4' }, { title: intl.formatMessage({ id: "ATTENDANCE.PERMISSIONS.CAMERA" }) })}
                                                    </div>
                                                </div>
                                        }
                                    </>
                                }
                                {formik.touched.attachment && formik.errors.attachment && (
                                    <div className='fv-plugins-message-container text-danger'>
                                        <span role='alert' className="text-danger">{formik.errors.attachment}</span>
                                    </div>
                                )}

                                {/* Note */}
                                <label className='form-label fs-6 fw-bolder text-dark'>{intl.formatMessage({ id: 'FORM.LABEL.NOTE' })}</label>
                                <div className="input-group mb-3">
                                    <textarea
                                        placeholder={intl.formatMessage({ id: 'FORM.LABEL.NOTE' })}
                                        id="floatingTextarea"
                                        {...formik.getFieldProps('note')}
                                        className={clsx(
                                            'form-control form-control-lg',
                                            { 'is-invalid': formik.touched.note && formik.errors.note },
                                        )}
                                        style={{ zIndex: 0 }}
                                        name="note"
                                        onInput={(e) => handleCaps(e)}
                                    >
                                    </textarea>
                                </div>
                                {formik.touched.note && formik.errors.note && (
                                    <div className='fv-plugins-message-container text-danger'>
                                        <span role='alert' className="text-danger">{formik.errors.note}</span>
                                    </div>
                                )}
                                {
                                    Object.keys(customFieldsMeratus).length > 0 && (
                                        <RenderCustomFieldGeneralForm
                                            customFieldGeneral={customFieldsMeratus}
                                            formik={formik}
                                            errors={formik.errors}
                                            containerRef={document.querySelector(`#update-visit-meeting-modal-${idMeeting}`) as HTMLElement}
                                            typeAction="update"
                                        />
                                    )
                                }
                                {
                                    Object.keys(customFieldsBaruBLA).length > 0 && (
                                        <RenderCustomFieldGeneralForm
                                            customFieldGeneral={customFieldsBaruBLA}
                                            formik={formik}
                                            errors={formik.errors}
                                            containerRef={document.querySelector(`#update-visit-meeting-modal-${idMeeting}`) as HTMLElement}
                                            typeAction="update"
                                        />
                                    )
                                }
                                {
                                    Object.keys(customFieldsLamaBLA).length > 0 && (
                                        <RenderCustomFieldGeneralForm
                                            customFieldGeneral={customFieldsLamaBLA}
                                            formik={formik}
                                            errors={formik.errors}
                                            containerRef={document.querySelector(`#update-visit-meeting-modal-${idMeeting}`) as HTMLElement}
                                            typeAction="update"
                                        />
                                    )
                                }
                                {
                                    customFields && Object.keys(customFields).map((key, index) => {
                                        if (key === '4af76875-862a-4533-b980-635f57853799') {

                                            const options = customFields[key].value.map((option, index) => ({
                                                value: option,
                                                label: option
                                            }))

                                            return (
                                                <>
                                                    <label className={`form-label fs-6 fw-bolder text-dark ${customFields[key].required && 'required'}`}>{customFields[key].name}</label>
                                                    <div className="input-group mb-3">
                                                        <Select
                                                            isClearable
                                                            options={options}
                                                            placeholder={`Pilih ${customFields[key].name}`}
                                                            value={options.find(option => option.value === formik.values[key as keyof typeof formik.values]) || null}
                                                            onChange={
                                                                option => formik.handleChange({
                                                                    target: {
                                                                        name: key,
                                                                        value: option?.value || ""
                                                                    }
                                                                })
                                                            }
                                                            onBlur={
                                                                () => formik.values[key as keyof typeof formik.values] === "" && formik.setFieldTouched(key, true)
                                                            }
                                                            className="w-100"
                                                        />
                                                    </div>
                                                    {
                                                        formik.touched[key as keyof typeof formik.touched] && formik.errors[key as keyof typeof formik.errors] &&
                                                        (
                                                            <div className='fv-plugins-message-container text-danger'>
                                                                <span role='alert' className="text-danger">{formik.errors[key as keyof typeof formik.errors]}</span>
                                                            </div>
                                                        )
                                                    }
                                                </>
                                            )
                                        }

                                        return null
                                    })
                                }
                            </div>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-light d-none"
                                    data-bs-dismiss="modal"
                                    ref={closeModalRef}
                                >
                                    {intl.formatMessage({ id: "FORM.ACTION.CANCEL" })}
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-light"
                                    onClick={handleCloseModal}
                                >
                                    {intl.formatMessage({ id: "FORM.ACTION.CANCEL" })}
                                </button>
                                <button type="submit" className={`btn btn-primary me-10 ${modalBtnLoading ? "disabled" : ""}`} data-kt-indicator={modalBtnLoading ? 'on' : 'off'}>
                                    <span className="indicator-label">
                                        {intl.formatMessage({ id: 'FORM.ACTION.SAVE' })}
                                    </span>
                                    <span className="indicator-progress">
                                        Loading... <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                                    </span>
                                </button>
                            </div>
                        </form>
                    </div>
                </div >
            </div >
        </>
    )
}

export { UpdateVisitMeeting }